import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ROLE_LABELS, isInternal, applyStatuses } from "@/lib/constants";
import { LayoutDashboard, FileText, Users2, Contact, LogOut, Menu, Moon, Sun, Upload, MapPin, Megaphone, CalendarDays, Package, Mail, Wallet, Bell, CheckCheck, FileSpreadsheet } from "lucide-react";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useExports } from "@/context/ExportsContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { openDrawer, activeCount } = useExports();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [unread, setUnread] = useState(0);
  const [pn, setPn] = useState({ unread: 0, items: [] });
  const [, setStatusTick] = useState(0);

  useEffect(() => {
    const loadStatuses = () => api.get("/meta").then((r) => { applyStatuses(r.data.status_list); setStatusTick((t) => t + 1); }).catch(() => {});
    loadStatuses();
    window.addEventListener("statuses-updated", loadStatuses);
    return () => window.removeEventListener("statuses-updated", loadStatuses);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const fetchUnread = () => {
      api.get("/comunicazioni/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
      api.get("/notifications/mine").then((r) => setPn(r.data)).catch(() => {});
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    window.addEventListener("comm-updated", fetchUnread);
    window.addEventListener("focus", fetchUnread);
    return () => { clearInterval(iv); window.removeEventListener("comm-updated", fetchUnread); window.removeEventListener("focus", fetchUnread); };
  }, []);

  const markAllComms = () => {
    api.post("/comunicazioni/read-all").then(() => { setUnread(0); window.dispatchEvent(new Event("comm-updated")); }).catch(() => {});
  };
  const markAllPn = () => {
    api.post("/notifications/read-all").then(() => setPn((s) => ({ unread: 0, items: s.items.map((i) => ({ ...i, read: true })) }))).catch(() => {});
  };
  const openPn = (n) => {
    if (!n.read) api.post(`/notifications/${n.id}/read`).catch(() => {});
    setPn((s) => ({ unread: Math.max(0, s.unread - (n.read ? 0 : 1)), items: s.items.map((i) => (i.id === n.id ? { ...i, read: true } : i)) }));
    if (n.practice_id) navigate(`/pratiche/${n.practice_id}`);
    else if (n.lead_id) navigate(`/marketing/rubrica/lead/${n.lead_id}`);
  };
  const totalUnread = unread + (pn.unread || 0);

  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/pratiche", label: "Pratiche", icon: FileText, testid: "nav-practices" },
    { to: "/anagrafiche", label: "Anagrafiche", icon: Contact, testid: "nav-anagrafiche" },
    { to: "/comunicazioni", label: "Comunicazioni", icon: Mail, testid: "nav-comunicazioni" },
  ];
  if (user?.role === "admin" || user?.role === "collaboratore") {
    nav.push({ to: "/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" });
    nav.push({ to: "/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-calendar" });
  }
  if (user?.role === "admin") nav.push({ to: "/amministrazione", label: "Amministrazione", icon: Wallet, testid: "nav-amministrazione" });
  if (user?.role === "segnalatore" || user?.role === "convenzionato" || user?.role === "istituto" || user?.role === "collaboratore") nav.push({ to: "/amministrazione", label: "Le mie provvigioni", icon: Wallet, testid: "nav-amministrazione" });
  if (user?.role === "admin") nav.push({ to: "/sedi", label: "Sedi", icon: MapPin, testid: "nav-sedi" });
  if (user?.role === "admin") nav.push({ to: "/prodotti", label: "Prodotti", icon: Package, testid: "nav-products" });
  if (user?.role === "admin" || user?.role === "collaboratore") nav.push({ to: "/utenti", label: user?.role === "admin" ? "Utenti" : "Convenzionati", icon: Users2, testid: "nav-users" });
  if (user?.role === "admin") nav.push({ to: "/importa", label: "Importa Dati", icon: Upload, testid: "nav-import" });

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-background">
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" data-testid="sidebar-backdrop" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-border bg-card flex flex-col transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex items-center gap-2.5 px-4 h-16 border-b border-border">
          <div className="flex items-center justify-center rounded-lg bg-white p-1 shrink-0 border border-border">
            <img src="/sms-logo.jpg" alt="SMS SRL" className="h-9 w-9 object-contain" data-testid="sidebar-logo" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm leading-tight truncate">SMS SRL</p>
            <p className="text-[10px] text-muted-foreground truncate">Servizi Finanziari</p>
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-2 border-t border-border space-y-1">
          <button
            data-testid="theme-toggle"
            onClick={() => setDark((d) => !d)}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {dark ? <Sun style={{ width: 18, height: 18 }} /> : <Moon style={{ width: 18, height: 18 }} />}
            <span>{dark ? "Tema Chiaro" : "Tema Scuro"}</span>
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut style={{ width: 18, height: 18 }} />
            <span>Esci</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-card/95 backdrop-blur px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" data-testid="sidebar-toggle" onClick={() => setMobileOpen((c) => !c)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1 max-w-md mr-2 sm:mr-4">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <button data-testid="exports-open-btn" onClick={openDrawer} className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" title="I miei export">
                <FileSpreadsheet className="h-5 w-5" />
                {activeCount > 0 && (
                  <span data-testid="exports-badge" className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-bold text-white">{activeCount}</span>
                )}
              </button>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="notif-bell" className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" title="Notifiche">
                  <Bell className="h-5 w-5" />
                  {totalUnread > 0 && (
                    <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">{totalUnread > 9 ? "9+" : totalUnread}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[22rem] max-w-[calc(100vw-1rem)] p-2" data-testid="notif-popover">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-sm font-semibold">Notifiche pratiche{pn.unread > 0 ? ` (${pn.unread})` : ""}</span>
                  <button data-testid="pn-mark-all" onClick={markAllPn} disabled={!pn.unread} className="text-xs text-primary hover:underline disabled:opacity-40 disabled:no-underline">Segna come lette</button>
                </div>
                <div className="max-h-72 overflow-y-auto" data-testid="pn-list">
                  {pn.items.length === 0 && <p className="px-2 py-3 text-xs text-muted-foreground">Nessuna notifica</p>}
                  {pn.items.map((n) => (
                    <button key={n.id} data-testid={`pn-item-${n.id}`} onClick={() => openPn(n)} className={`flex w-full gap-2 rounded-md px-2 py-2 text-left hover:bg-accent ${n.read ? "opacity-60" : ""}`}>
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-primary"}`} />
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold truncate">{n.heading} · {n.numero} {n.client && `· ${n.client}`}</span>
                        <span className="block text-xs text-muted-foreground line-clamp-2">{n.text}</span>
                        <span className="block text-[10px] text-muted-foreground">{n.created_at ? new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="my-1 border-t" />
                <div className="px-2 py-1.5 text-sm font-semibold">{unread > 0 ? `${unread} comunicazioni non lette` : "Nessuna comunicazione non letta"}</div>
                <button data-testid="notif-mark-all" onClick={markAllComms} disabled={unread === 0} className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed">
                  <CheckCheck className="h-4 w-4" /> Segna tutte come lette
                </button>
                <button data-testid="notif-open" onClick={() => navigate("/comunicazioni")} className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent">
                  <Mail className="h-4 w-4" /> Apri comunicazioni
                </button>
              </PopoverContent>
            </Popover>
            <div className="text-right">
              <p className="text-sm font-semibold leading-tight" data-testid="current-user-name">{user?.name}</p>
              <p className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
            </div>
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white ${isInternal(user?.role) ? "bg-primary" : "bg-amber-500"}`}>
              {user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
